/**
 * Discard a session's in-progress collected data.
 *
 * Used when a data-collection page is RELOADED mid-session (see the reload heuristic in
 * the collection screens). Product decision: a refresh starts the session over, so prior
 * entries are cleared for BOTH the collector and any supervisor watching the live view —
 * rather than leaving a blank collection screen out of step with server data that is
 * still visible to the supervisor.
 *
 * Two places hold that data and both must be cleared, or it reappears:
 *   1. The server rows — SOFT-deleted via the scoped discard-own endpoint, which removes
 *      ONLY the caller's own rows and only while the session is open (recoverable from the
 *      DB by an admin). It is gated on datacollection.update, NOT the supervisory
 *      datacollection.delete a therapist lacks — so a therapist's reload can actually clear
 *      its own pre-refresh rows, where the generic per-row DELETE used to 403 and leave
 *      them visible to the supervisor. Soft-deleted rows are hidden from the supervisor's
 *      GET and from the offline_id upsert lookup, so a later re-collection creates a fresh,
 *      visible row.
 *   2. The local offline queue — removed, or background sync would re-POST them and
 *      resurrect the rows.
 *
 * Best-effort and never throws: a cleared screen must still finish loading even if the
 * discard fails. Returns how many server rows were removed.
 *
 * @param {string} sessionId - session UUID
 * @param {object} [opts]
 * @param {string} [opts.collectedById] - accepted for call-site compatibility. Ownership is
 *   now enforced server-side against the authenticated collector (which IS the person
 *   reloading), so this is informational only and no longer used to filter client-side.
 */
import { discardOwnSessionData } from 'services/api/sessions';
import { getAllOfflineEntries, deleteOfflineEntry } from './offlineDB';

export const discardSessionCollectedData = async (sessionId, { collectedById = null } = {}) => {
  if (!sessionId) return { deleted: 0 };
  // collectedById is accepted for call-site compatibility but no longer used: ownership is
  // enforced server-side against the authenticated collector (the person reloading).
  let deleted = 0;

  // 1. Soft-delete the caller's own server rows for this session, in one scoped call.
  try {
    const res = await discardOwnSessionData(sessionId);
    // DeleteResponse.message is "Discarded N record(s)"; pull N for the caller's snackbar.
    const match = /Discarded\s+(\d+)/.exec(res?.message || '');
    deleted = match ? Number(match[1]) : 0;
  } catch (err) {
    // A failure here must not block the screen from loading. The most likely non-success
    // is a 409 (session already closed) — nothing to discard in that case anyway.
    console.error('[discardSessionData] server discard failed:', err);
  }

  // 2. Drop local queue entries for this session so background sync cannot re-create them.
  try {
    const all = await getAllOfflineEntries();
    const mine = all.filter((e) => String(e.sessionId) === String(sessionId));
    for (const e of mine) {
      try {
        await deleteOfflineEntry(e.id);
      } catch (err) {
        console.error('[discardSessionData] local delete failed for', e.id, err);
      }
    }
  } catch (err) {
    console.error('[discardSessionData] could not clear local entries:', err);
  }

  return { deleted };
};

export default discardSessionCollectedData;
